/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import clsx from 'clsx'

interface BatchSelectOption {
    value: string
    label: string
}

interface UiTanstackBatchSelectControlProps {
    options: BatchSelectOption[]
    selectedValue: string
    onSelectedValueChange: (value: string) => void
    onApply: () => void
    selectPlaceholder: string
    applyLabel: string
    disabled?: boolean
}

const UiTanstackBatchSelectControl: React.FC<UiTanstackBatchSelectControlProps> = ({
    options,
    selectedValue,
    onSelectedValueChange,
    onApply,
    selectPlaceholder,
    applyLabel,
    disabled,
}) => {
    return (
        <div
            className={clsx(
                'UiTanstackBatchSelectControl flex w-full min-w-[170px] items-center gap-1.5 rounded-md border border-gray-200/80',
                'bg-gray-50/80 px-1.5 py-1 dark:border-gray-700/80 dark:bg-gray-900/50'
            )}
        >
            <select
                value={selectedValue}
                onChange={(event) => onSelectedValueChange(event.target.value)}
                disabled={disabled}
                className={clsx(
                    'h-7 min-w-[110px] flex-1 rounded-md border border-gray-300 bg-white px-1.5 text-xs',
                    'text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/50',
                    'disabled:cursor-not-allowed disabled:opacity-60',
                    'dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100'
                )}
            >
                <option value="">{selectPlaceholder}</option>
                {options.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>

            <button
                type="button"
                onClick={onApply}
                disabled={disabled || !selectedValue}
                className={clsx(
                    'h-7 rounded-md bg-blue-600 px-2 text-xs font-medium text-white transition-colors',
                    'hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-400 disabled:text-gray-100',
                    'dark:disabled:bg-gray-700'
                )}
            >
                {applyLabel}
            </button>
        </div>
    )
}

export default UiTanstackBatchSelectControl
